import type { DiligenceArtifact, DiligenceTemplateSummary, Message } from '../types';

export function getMessageArtifacts(message: Pick<Message, 'artifact' | 'artifacts'>) {
  const artifacts = [...(message.artifacts || []), ...(message.artifact ? [message.artifact] : [])];
  return artifacts.filter((artifact, index) => artifacts.findIndex(item => item.id === artifact.id) === index);
}

export const getActiveDocumentGeneration = (messages: Message[]) => (
  [...messages].reverse().find((message) => (
    message.sender === 'bot'
    && message.documentRun?.status === 'running'
    && typeof message.sourceClientMessageId === 'string'
  ))
);

export type LegacyTemplateStatus = DiligenceTemplateSummary['status'] | 'needs_review' | 'unsupported';

export const normalizeTemplateStatus = (status: unknown): DiligenceTemplateSummary['status'] => {
  switch (status as LegacyTemplateStatus) {
    case 'uploaded':
    case 'processing':
    case 'ready':
    case 'review_required':
    case 'degraded':
    case 'failed':
      return status as DiligenceTemplateSummary['status'];
    case 'needs_review':
      return 'review_required';
    case 'unsupported':
      return 'degraded';
    default:
      return 'processing';
  }
};

export const getTemplateStatusPresentation = (
  status: DiligenceTemplateSummary['status'],
  reason?: string
) => {
  switch (status) {
    case 'uploaded':
    case 'processing':
      return {
        label: 'Procesando',
        description: 'Juris está preparando la plantilla para poder utilizarla.',
        tone: 'processing' as const
      };
    case 'ready':
      return {
        label: 'Lista',
        description: 'La plantilla está preparada para utilizarse en Instruir.',
        tone: 'ready' as const
      };
    case 'review_required':
      return {
        label: 'Revisión necesaria',
        description: reason?.trim() || 'Juris necesita confirmar qué zonas puede editar antes de utilizarla.',
        tone: 'review' as const
      };
    case 'degraded':
      return {
        label: 'Compatibilidad limitada',
        description: reason?.trim() || 'Juris puede usarla como referencia, pero no garantizar el mismo formato.',
        tone: 'degraded' as const
      };
    case 'failed':
      return {
        label: 'Error al preparar',
        description: reason?.trim() || 'No se pudo preparar esta plantilla. Sube una versión nueva para reintentarlo.',
        tone: 'failed' as const
      };
  }
};

export const getTemplateUsability = (
  status: DiligenceTemplateSummary['status'],
  explicit?: unknown
): DiligenceTemplateSummary['usability'] => {
  if (explicit === 'processing' || explicit === 'usable' || explicit === 'incompatible') return explicit;
  if (status === 'uploaded' || status === 'processing') return 'processing';
  return status === 'failed' ? 'incompatible' : 'usable';
};

export const getArtifactPresentation = (artifact: DiligenceArtifact) => {
  const format = artifact.contentType === 'application/pdf' ? 'PDF' : 'Word';
  return { format };
};
